import React, { createContext, useContext, useState, useEffect } from 'react';
import { onboardingSteps, TOUR_VERSION, getTourKeyForPath, TourStep } from '../data/onboardingSteps';
import { useLocation } from 'react-router-dom';

interface OnboardingContextType {
    isActive: boolean;
    currentStepIndex: number;
    showPrompt: boolean;
    activeSteps: TourStep[];
    startTour: () => void;
    skipTour: () => void;
    nextStep: () => void;
    prevStep: () => void;
    endTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isActive, setIsActive] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [showPrompt, setShowPrompt] = useState(false);
    const [activeSteps, setActiveSteps] = useState<TourStep[]>([]);
    const location = useLocation();

    // Check for tour on path change
    useEffect(() => {
        const path = location.pathname;
        const relevantSteps = onboardingSteps.filter(step =>
            !step.path ||
            step.path === path ||
            (step.path === 'wizard' && path.includes('wizard'))
        );

        if (relevantSteps.length === 0) {
            setShowPrompt(false);
            return;
        }

        setActiveSteps(relevantSteps);

        // Auto-prompt is disabled — the guided tour is now opt-in only.
        // Users can start it manually via the Help/? button.
        // (The tour infrastructure and steps are fully preserved.)
        setShowPrompt(false);

    }, [location.pathname]);

    const startTour = () => {
        setIsActive(true);
        setShowPrompt(false);
        setCurrentStepIndex(0);
    };

    const skipTour = () => {
        setShowPrompt(false);
        const tourKey = getTourKeyForPath(location.pathname);
        localStorage.setItem(tourKey, String(TOUR_VERSION));
        window.dispatchEvent(new Event('onboardingTourCompleted'));
    };

    const endTour = () => {
        setIsActive(false);
        const tourKey = getTourKeyForPath(location.pathname);
        localStorage.setItem(tourKey, String(TOUR_VERSION));
        window.dispatchEvent(new Event('onboardingTourCompleted'));
    };

    const nextStep = () => {
        if (currentStepIndex < activeSteps.length - 1) {
            setCurrentStepIndex((prev: number) => prev + 1);
        } else {
            endTour();
        }
    };

    const prevStep = () => {
        if (currentStepIndex > 0) {
            setCurrentStepIndex((prev: number) => prev - 1);
        }
    };

    return (
        <OnboardingContext.Provider value={{
            isActive,
            currentStepIndex,
            showPrompt,
            activeSteps,
            startTour,
            skipTour,
            nextStep,
            prevStep,
            endTour
        }}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (context === undefined) {
        throw new Error('useOnboarding must be used within an OnboardingProvider');
    }
    return context;
};
